import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import { useUser } from "../context/UserContext";
import { teams as teamsService, kits as kitsService, teamMembers, teamInvitations } from "../services/supabase";
import { Team, Kit, TeamMember } from "../types/database";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./team-portal.css";

const apiUrl = process.env.REACT_APP_API_URL;

const TeamPortal: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { user, username } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [team, setTeam] = useState<Team | null>(null);
  const [kits, setKits] = useState<Kit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingKits, setLoadingKits] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || "kit");
  const [deletingKitId, setDeletingKitId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [kitToDelete, setKitToDelete] = useState<string | null>(null);
  const [editingKitId, setEditingKitId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState<string>("");
  const [kitImageView, setKitImageView] = useState<Record<string, 'front' | 'back'>>({});
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [sendingInvite, setSendingInvite] = useState<boolean>(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [userRole, setUserRole] = useState<'captain' | 'member' | null>(null);
  const [showDeleteTeamConfirm, setShowDeleteTeamConfirm] = useState<boolean>(false);
  const [deleteTeamInput, setDeleteTeamInput] = useState<string>("");
  const [deletingTeam, setDeletingTeam] = useState<boolean>(false);
  const [editingPitch, setEditingPitch] = useState<boolean>(false);
  const [editedSummary, setEditedSummary] = useState<string>("");
  const [savingPitch, setSavingPitch] = useState<boolean>(false);

  useEffect(() => {
    if (userRole === 'member' && (activeTab === 'sponsorship' || activeTab === 'members')) {
      setActiveTab('kit');
    }
  }, [userRole, activeTab]);

  useEffect(() => {
    if (!user || !teamId) {
      setLoading(false);
      return;
    }

    const fetchTeam = async () => {
      try {
        const { data: teamData, error: teamError } = await teamsService.getById(teamId);
        if (teamError) throw teamError;

        if (!teamData) {
          setTeam(null);
          setLoading(false);
          return;
        }

        const { data: memberData, error: memberError } = await teamMembers.getByTeam(teamId);
        if (memberError) console.error("Error fetching members:", memberError);

        const currentMember = memberData?.find((m: TeamMember) => m.user_id === user.id);
        const isMember = !!currentMember;
        const isCreator = teamData.created_by === user.id;

        if (!isCreator && !isMember) {
          console.log("Access check failed:", { isCreator, isMember, userId: user.id, teamCreator: teamData.created_by });
          setTeam(null);
          setError("You don't have access to this team");
        } else {
          setTeam(teamData);
          setUserRole(currentMember?.role || (isCreator ? 'captain' : 'member'));
        }
      } catch (err) {
        console.error("Failed to fetch team:", err);
        setError("Failed to load team");
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId, user]);

  useEffect(() => {
    if (!teamId) {
      setLoadingKits(false);
      return;
    }

    const fetchKits = async () => {
      try {
        const { data, error } = await kitsService.getByTeam(teamId);
        if (error) throw error;
        setKits(data || []);

        const initialViews: Record<string, 'front' | 'back'> = {};
        data?.forEach((kit: Kit) => { initialViews[kit.kit_id] = 'front'; });
        setKitImageView(initialViews);
      } catch (err) {
        console.error("Failed to fetch kits:", err);
      } finally {
        setLoadingKits(false);
      }
    };

    fetchKits();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;

    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        const { data, error } = await teamMembers.getByTeam(teamId);
        if (error) throw error;
        setMembers(data || []);
      } catch (err) {
        console.error("Failed to fetch members:", err);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [teamId]);

  const toggleKitImage = (kitId: string) => {
    setKitImageView(prev => ({
      ...prev,
      [kitId]: prev[kitId] === 'front' ? 'back' : 'front'
    }));
  };

  const handleDeleteKit = async () => {
    if (!kitToDelete) return;
    setDeletingKitId(kitToDelete);
    setShowDeleteConfirm(false);

    try {
      const { error } = await kitsService.delete(kitToDelete);
      if (error) throw error;
      setKits(kits.filter(k => k.kit_id !== kitToDelete));
    } catch (err) {
      console.error("Failed to delete kit:", err);
      alert(`Failed to delete kit: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingKitId(null);
      setKitToDelete(null);
    }
  };

  const confirmDelete = (kitId: string) => {
    setKitToDelete(kitId);
    setShowDeleteConfirm(true);
  };

  const startEditingName = (kitId: string, currentName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingKitId(kitId);
    setEditedName(currentName);
  };

  const handleUpdateKitName = async (kitId: string) => {
    if (!editedName.trim()) {
      alert("Kit name cannot be empty");
      return;
    }

    try {
      const { error } = await kitsService.update(kitId, { design_name: editedName });
      if (error) throw error;
      setKits(kits.map(k => k.kit_id === kitId ? { ...k, design_name: editedName } : k));
      setEditingKitId(null);
    } catch (err) {
      console.error("Failed to update kit name:", err);
      alert(`Failed to update kit name: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const cancelEditing = () => {
    setEditingKitId(null);
    setEditedName("");
  };

  const handleSavePitch = async () => {
    if (!team) return;
    setSavingPitch(true);
    try {
      const { error } = await teamsService.update(team.id, { summary: editedSummary });
      if (error) throw error;
      setTeam({ ...team, summary: editedSummary });
      setEditingPitch(false);
    } catch (err) {
      console.error("Failed to update pitch:", err);
      alert(`Failed to save pitch: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSavingPitch(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !user || !team) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      alert("Please enter a valid email address");
      return;
    }

    setSendingInvite(true);

    try {
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { error } = await teamInvitations.create({
        team_id: teamId!,
        invited_email: inviteEmail,
        invited_by: user.id,
        status: 'pending',
        token: token,
        expires_at: expiresAt.toISOString(),
        accepted_at: null
      });

      if (error) throw error;

      const invitationLink = `${window.location.origin}/account?invite=${token}`;

      const response = await fetch(`${apiUrl}/api/send-team-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invited_email: inviteEmail,
          team_name: team.team_name,
          inviter_name: username,
          invitation_link: invitationLink
        })
      });

      if (!response.ok) throw new Error('Failed to send invitation email');

      alert(`Invitation sent to ${inviteEmail}!`);
      setInviteEmail("");
      setShowInviteModal(false);
    } catch (err) {
      console.error("Failed to send invitation:", err);
      alert(`Failed to send invitation: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!team || !user || deleteTeamInput !== team.team_name) return;
    setDeletingTeam(true);

    try {
      const teamSnapshot = {
        team_name: team.team_name,
        summary: team.summary || '',
        logo_url: team.logo_url || ''
      };

      let logoBase64 = '';
      if (teamSnapshot.logo_url) {
        try {
          const response = await fetch(teamSnapshot.logo_url);
          const blob = await response.blob();
          logoBase64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.error("Failed to convert logo to base64:", err);
        }
      }

      const { data: memberData } = await teamMembers.getByTeam(team.id);
      const memberList = (memberData || []).map((m: TeamMember) => ({
        name: m.profile?.full_name || m.profile?.email || 'Unknown',
        email: m.profile?.email || '',
        role: m.role,
        tshirt_size: m.profile?.tshirt_size || ''
      }));

      const { error } = await teamsService.delete(team.id);
      if (error) throw error;

      fetch(`${apiUrl}/api/send-team-deleted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          captain_email: user.email,
          captain_name: username,
          team_name: teamSnapshot.team_name,
          summary: teamSnapshot.summary,
          logo_base64: logoBase64,
          members: memberList
        })
      }).then(res => { if (!res.ok) console.warn('Team deletion email failed:', res.status); })
        .catch(err => console.error("Failed to send deletion email:", err));

      navigate("/", { state: { scrollTo: "tribes" } });
    } catch (err: any) {
      console.error("Failed to delete team:", err);
      const message = err?.message || err?.error_description || err?.details || JSON.stringify(err);
      alert(`Failed to delete team: ${message}`);
    } finally {
      setDeletingTeam(false);
    }
  };

  if (loading) {
    return (
      <div className="team-portal-page">
        <div className="grain-overlay" />
        <SiteNav />
        <div className="tp-content-wrap">
          <p className="tp-loading">Loading team...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-portal-page">
        <div className="grain-overlay" />
        <SiteNav />
        <div className="tp-content-wrap">
          <div className="tp-not-found">
            <div className="tp-not-found-num">404</div>
            <h2 className="tp-not-found-title">{error || "Team Not Found"}</h2>
            <p className="tp-not-found-desc">
              {error === "You don't have access to this team"
                ? "This team is private. You need an invitation to view it."
                : "The team you're looking for doesn't exist."}
            </p>
            <button
              onClick={() => navigate("/", { state: { scrollTo: "tribes" } })}
              className="tp-btn-primary"
            >
              Back to My Teams
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-portal-page">
      <div className="grain-overlay" />
      <SiteNav />

      <div className="tp-content-wrap">

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="tp-modal-overlay">
          <div className="tp-modal">
            <h3 className="tp-modal-title">Invite Team Member</h3>
            <p className="tp-modal-body">
              Enter the email address of the person you'd like to invite to {team?.team_name}.
            </p>
            <div className="tp-modal-label">Email address</div>
            <input
              type="email"
              placeholder="member@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="tp-modal-input"
              disabled={sendingInvite}
            />
            <div className="tp-modal-actions">
              <button
                onClick={() => { setShowInviteModal(false); setInviteEmail(""); }}
                className="tp-btn-secondary"
                disabled={sendingInvite}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={!inviteEmail.trim() || sendingInvite}
                className="tp-btn-primary"
              >
                {sendingInvite ? "Sending..." : "Send Invite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Kit Confirmation */}
      {showDeleteConfirm && (
        <div className="tp-modal-overlay">
          <div className="tp-modal danger">
            <h3 className="tp-modal-title danger">Delete Kit Design?</h3>
            <p className="tp-modal-body">
              This will permanently delete this kit design. This action cannot be undone.
            </p>
            <div className="tp-modal-actions">
              <button
                onClick={() => { setShowDeleteConfirm(false); setKitToDelete(null); }}
                className="tp-btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleDeleteKit} className="tp-btn-danger">
                Delete Kit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Team Confirmation */}
      {showDeleteTeamConfirm && (
        <div className="tp-modal-overlay">
          <div className="tp-modal danger">
            <h3 className="tp-modal-title danger">Delete Team</h3>
            <p className="tp-modal-body">
              This will permanently delete <strong>{team?.team_name}</strong> and all associated kit designs, members, and invitations. This cannot be undone.
            </p>
            <div className="tp-modal-label">
              Type <strong>{team?.team_name}</strong> to confirm:
            </div>
            <input
              type="text"
              value={deleteTeamInput}
              onChange={(e) => setDeleteTeamInput(e.target.value)}
              placeholder={team?.team_name}
              className="tp-modal-input"
              disabled={deletingTeam}
            />
            <div className="tp-modal-actions">
              <button
                onClick={() => { setShowDeleteTeamConfirm(false); setDeleteTeamInput(""); }}
                className="tp-btn-secondary"
                disabled={deletingTeam}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTeam}
                disabled={deleteTeamInput !== team?.team_name || deletingTeam}
                className="tp-btn-danger"
              >
                {deletingTeam ? "Deleting..." : "Delete Team"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back button */}
      <button
        onClick={() => navigate("/", { state: { scrollTo: "tribes" } })}
        className="tp-back"
      >
        ← My Teams
      </button>

      {/* Team Header */}
      <div className="tp-header">
        {team.logo_url ? (
          <img src={team.logo_url} alt={`${team.team_name} logo`} className="tp-logo" />
        ) : (
          <div className="tp-logo-placeholder">
            {team.team_name.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="tp-header-info">
          <span className={`tp-role-badge ${userRole}`}>
            {userRole === 'captain' ? 'Captain' : 'Member'}
          </span>
          <h1 className="tp-team-name">{team.team_name}</h1>
          <p className="tp-team-id">ID: {team.id}</p>
        </div>

        {userRole === 'captain' && (
          <button
            onClick={() => setShowDeleteTeamConfirm(true)}
            className="tp-delete-btn"
            title="Delete team"
          >
            <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tp-tabs">
        <button
          onClick={() => setActiveTab("kit")}
          className={`tp-tab ${activeTab === "kit" ? "active" : ""}`}
        >
          Kit
        </button>
        {userRole === 'captain' ? (
          <button
            onClick={() => setActiveTab("members")}
            className={`tp-tab ${activeTab === "members" ? "active" : ""}`}
          >
            Members
          </button>
        ) : (
          <button
            onClick={() => setActiveTab("broadcasts")}
            className={`tp-tab ${activeTab === "broadcasts" ? "active" : ""}`}
          >
            Broadcasts
          </button>
        )}
        {userRole === 'captain' && (
          <button
            onClick={() => setActiveTab("sponsorship")}
            className={`tp-tab ${activeTab === "sponsorship" ? "active" : ""}`}
          >
            Sponsorship
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div>

        {/* Sponsorship */}
        {activeTab === "sponsorship" && (
          <div>
            <div className="tp-section-header">
              <div className="tp-section-num">[ SPONSORSHIP ]</div>
              <h2 className="tp-section-title">Your Pitch</h2>
            </div>
            <p className="tp-desc">
              Manage your team's sponsorship opportunities and attract brand partners.
            </p>

            <div className="tp-pitch-card">
              <div className="tp-pitch-label-row">
                <div className="tp-pitch-label">Sponsorship Pitch</div>
                {!editingPitch && (
                  <button
                    className="tp-kit-edit-btn"
                    onClick={() => { setEditedSummary(team.summary || ""); setEditingPitch(true); }}
                    aria-label="Edit pitch"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
              </div>

              {editingPitch ? (
                <>
                  <textarea
                    className="tp-pitch-textarea"
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    rows={6}
                    disabled={savingPitch}
                    placeholder="Describe your team and what makes you a great sponsorship opportunity…"
                  />
                  <div className="tp-edit-actions">
                    <button
                      onClick={() => setEditingPitch(false)}
                      className="tp-edit-cancel"
                      disabled={savingPitch}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePitch}
                      className="tp-edit-save"
                      disabled={savingPitch}
                    >
                      {savingPitch ? "Saving…" : "Save"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="tp-pitch-text">
                  {team.summary || <span style={{ opacity: 0.35, fontStyle: 'italic' }}>No pitch written yet. Click the edit icon to add one.</span>}
                </p>
              )}
            </div>

            <button className="tp-btn-primary">+ Find Sponsors</button>
          </div>
        )}

        {/* Members */}
        {activeTab === "members" && userRole === 'captain' && (
          <div>
            <div className="tp-section-header">
              <div className="tp-section-num">[ MEMBERS ]</div>
              <h2 className="tp-section-title">Your Lineup</h2>
            </div>
            <p className="tp-desc">
              Manage your squad and invite new members to join.
            </p>

            {loadingMembers ? (
              <p className="tp-loading">Loading members...</p>
            ) : members.length > 0 ? (
              <div className="tp-members-list">
                <div className="tp-members-header">
                  Roster — {members.length} player{members.length !== 1 ? 's' : ''}
                </div>
                {members.map((member: TeamMember) => (
                  <div key={member.id} className="tp-member-row">
                    <div className="tp-member-avatar">
                      {member.profile?.full_name?.charAt(0) || member.profile?.email?.charAt(0) || '?'}
                    </div>
                    <div className="tp-member-info">
                      <div className="tp-member-name">
                        {member.profile?.full_name || member.profile?.email || 'Unknown'}
                      </div>
                      <div className="tp-member-meta">
                        {member.profile?.tshirt_size && `Size: ${member.profile.tshirt_size}`}
                      </div>
                    </div>
                    {member.role === 'captain' ? (
                      <span className="tp-member-captain-badge">Captain</span>
                    ) : (
                      <button className="tp-member-remove">Remove</button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="tp-empty">
                <div className="tp-empty-num">00</div>
                <div className="tp-empty-title">No Members Yet</div>
                <p className="tp-empty-desc">Invite your first teammate to get started.</p>
              </div>
            )}

            <button
              onClick={() => setShowInviteModal(true)}
              className="tp-btn-primary"
            >
              + Add Member
            </button>
          </div>
        )}

        {/* Broadcasts */}
        {activeTab === "broadcasts" && userRole === 'member' && (
          <div>
            <div className="tp-section-header">
              <div className="tp-section-num">[ BROADCASTS ]</div>
              <h2 className="tp-section-title">Updates</h2>
            </div>
            <p className="tp-desc">
              Announcements and updates from your team captain.
            </p>
            <div className="tp-empty">
              <div className="tp-empty-num">00</div>
              <div className="tp-empty-title">No Broadcasts Yet</div>
              <p className="tp-empty-desc">Your captain will post updates here.</p>
            </div>
          </div>
        )}

        {/* Kit */}
        {activeTab === "kit" && (
          <div>
            <div className="tp-section-header">
              <div className="tp-section-num">[ KIT ]</div>
              <h2 className="tp-section-title">Your Kit</h2>
            </div>
            <p className="tp-desc">
              Design and order custom teamwear for your squad.
            </p>

            {loadingKits ? (
              <p className="tp-loading">Loading kits...</p>
            ) : kits.length > 0 ? (
              <div className="tp-kits-grid">
                {kits.map((kit) => {
                  const currentView = kitImageView[kit.kit_id] || 'front';
                  const showingFront = currentView === 'front';
                  const currentImageUrl = showingFront ? kit.preview_front_url : kit.preview_back_url;

                  return (
                    <div key={kit.id} className="tp-kit-card">
                      <div className="tp-kit-preview">
                        {currentImageUrl ? (
                          <img
                            src={currentImageUrl}
                            alt={`Kit ${showingFront ? 'Front' : 'Back'} Preview`}
                          />
                        ) : (
                          <div style={{
                            background: kit.teamwear_color || '#1E3A8A',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <span style={{
                              color: 'rgba(255,255,255,0.7)',
                              fontFamily: 'JetBrains Mono, monospace',
                              fontSize: '0.72rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.12em'
                            }}>
                              {kit.kit_type === 'polo' ? 'Polo Shirt' : 'T-Shirt'}
                            </span>
                          </div>
                        )}

                        {kit.preview_front_url && kit.preview_back_url && (
                          <>
                            <button
                              onClick={() => toggleKitImage(kit.kit_id)}
                              disabled={showingFront}
                              className="tp-kit-nav-btn left"
                              aria-label="Show front view"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              onClick={() => toggleKitImage(kit.kit_id)}
                              disabled={!showingFront}
                              className="tp-kit-nav-btn right"
                              aria-label="Show back view"
                            >
                              <ChevronRight size={18} />
                            </button>
                            <div className="tp-kit-view-badge">
                              {showingFront ? 'Front' : 'Back'}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="tp-kit-info">
                        {editingKitId === kit.kit_id ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editedName}
                              onChange={(e) => setEditedName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleUpdateKitName(kit.kit_id);
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              className="tp-kit-name-edit"
                              autoFocus
                            />
                            <div className="tp-edit-actions">
                              <button onClick={() => handleUpdateKitName(kit.kit_id)} className="tp-edit-save">Save</button>
                              <button onClick={cancelEditing} className="tp-edit-cancel">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="tp-kit-name-row">
                            <span className="tp-kit-name">{kit.design_name}</span>
                            <button
                              onClick={(e) => startEditingName(kit.kit_id, kit.design_name, e)}
                              className="tp-kit-edit-btn"
                              aria-label="Edit kit name"
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </div>
                        )}

                        <div className="tp-kit-date">
                          Created {new Date(kit.created_at).toLocaleDateString()}
                        </div>

                        <div className="tp-kit-tags">
                          <span>{kit.kit_type === 'polo' ? 'Polo' : 'T-Shirt'}</span>
                          {kit.front_image_show && <span>· Front Logo</span>}
                          {kit.back_image_show && <span>· Back Logo</span>}
                          {kit.back_print_enabled && kit.back_print_text && <span>· "{kit.back_print_text}"</span>}
                        </div>

                        <div className="tp-kit-actions">
                          {userRole === 'captain' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); confirmDelete(kit.kit_id); }}
                              disabled={deletingKitId === kit.kit_id}
                              className="tp-kit-action-btn delete"
                            >
                              {deletingKitId === kit.kit_id ? "Deleting..." : "Delete"}
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/order?kitId=${kit.kit_id}`)}
                            className="tp-kit-action-btn order"
                          >
                            Order
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="tp-empty">
                <div className="tp-empty-num">00</div>
                <div className="tp-empty-title">No Kit Designs Yet</div>
                <p className="tp-empty-desc">Create your first custom kit design to get started.</p>
              </div>
            )}

            {userRole === 'captain' && (
              <button
                onClick={() => navigate(`/create-kit?teamId=${team.id}`)}
                className="tp-btn-primary"
              >
                + Create New Kit Design
              </button>
            )}

            {userRole === 'member' && kits.length === 0 && (
              <p className="tp-desc" style={{ textAlign: 'center', marginTop: '1rem' }}>
                Your captain hasn't created any kit designs yet. Check back soon!
              </p>
            )}
          </div>
        )}

      </div>

      </div>{/* tp-content-wrap */}
    </div>
  );
};

export default TeamPortal;
